/*
 * decaffeinate suggestions:
 * DS101: Remove unnecessary use of Array.from
 * DS102: Remove unnecessary code created because of implicit returns
 * DS206: Consider reworking classes to avoid initClass
 * DS207: Consider shorter variations of null checks
 * Full docs: https://github.com/decaffeinate/decaffeinate/blob/master/docs/suggestions.md
 */
import Backbone from 'backbone';
import classNames from 'classnames';
import React from 'react';
import ReactDOMServer from 'react-dom/server';
import {debounce} from 'ts-debounce';
import _ from 'underscore';

import {ArchiveRecord} from 'archive/ArchiveConstants';
import {assert} from 'Asserts';
import {
  DNA_COMPLEMENT_TABLE,
  IUPAC,
  IupacPartialMapping,
  NucleotideType,
  NUCLEOTIDE_TYPE_TO_COMPLEMENT_TABLE,
  RNA_COMPLEMENT_TABLE,
} from 'biojs/BioConstants';
import {Sequence} from 'biojs/Sequence';
import * as util from 'biojs/Util';
import {Sequence as ComponentSequence} from 'components/Sequence';
import SequenceAttributeCollection from 'components/SequenceAttributeCollection';
import {NumberIDFolder} from 'constants/FolderConstants';
import {OPPOSITE_STRAND_OF_DOUBLE_STRANDED, StrandOfDoubleStranded} from 'constants/MolbioConstants';
import {MAX_PRIMER_NAME_LENGTH} from 'constants/PrimerConstants';
import {
  ModifiedNucleotideAttributes,
  PolymerFeatureAttributes,
  PolymerFeatureModel,
  SequenceType,
} from 'constants/SequenceConstants';
import * as colors from 'lib/Colors';
import {getUTFlexibleNaturalBasesIfEnabled} from 'modbio/util';
import * as sequtil from 'molbio/SeqUtil';
import {ellipsize} from 'util/NameUtil';

// Must match primer.py
const MIN_PRIMER_LENGTH = 6;

const DIRECTION_ABBR = {
  forward: 'FWD',
  reverse: 'REV',
  probe: 'PROBE',
};

export type Direction = 'forward' | 'reverse' | 'probe';

export interface SequencePrimerAttributes extends PolymerFeatureAttributes {
  archiveRecord?: ArchiveRecord | null;
  bases: string;
  color: string;
  // TODO(BNCH-36838) Remove this attr. Used only in Find Primers flow; disables search result row when true
  disabled?: boolean;
  // Old versions may not have this value populated
  displayLabel?: string;
  folder: NumberIDFolder | string;
  folder_api_id?: string;
  folder_url?: string;
  modifiedNucleotides: Readonly<Array<Readonly<ModifiedNucleotideAttributes>>>;
  name: string;
  nucleotideType: NucleotideType;
  overhang_length: number;
  owner?: string;
  primer_api_id?: string | null;
  primer_id: number | null;
  // TODO(BNCH-36838) Remove this attr. Used only in Find Primers flow; controls which search result is chosen
  selected?: boolean;
  // this is only used by EditPrimerTabState
  sequenceID?: string;
  // Need to constrain since sequence primers cannot have a strand value of 0.
  strand: StrandOfDoubleStranded;
}

type GetAttribute<A extends keyof SequencePrimerAttributes> = SequencePrimerAttributes[A];

interface MinimalFolderSpecification {
  name: string;
  api_identifier: string;
  url: string;
}

// see Backbone.sync in backbone-extensions.js for additional options
interface SaveOptions extends Backbone.ModelSaveOptions {
  fileUpdate?: boolean;
}

interface SequencePrimer {
  attributes: SequencePrimerAttributes;
  get<K extends keyof SequencePrimerAttributes>(key: K): SequencePrimerAttributes[K];
  set(obj: Partial<SequencePrimerAttributes>): SequencePrimer;
  set<K extends keyof SequencePrimerAttributes>(key: K, value: SequencePrimerAttributes[K]): SequencePrimer;
  fastSet(obj: Partial<SequencePrimerAttributes>): SequencePrimer;
  fastSet<K extends keyof SequencePrimerAttributes>(
    key: K,
    value: SequencePrimerAttributes[K]
  ): SequencePrimer;
  fastUnset<K extends keyof SequencePrimerAttributes>(key: K): SequencePrimer;
  save(attributes?: null | Partial<SequencePrimerAttributes>, options?: SaveOptions): false | JQueryXHR;
  // Part modification code hackily tacks on these booleans sometimes.
  isDirectStart?: boolean;
  isDirectEnd?: boolean;
}

class SequencePrimer extends Backbone.Model implements PolymerFeatureModel {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  debouncedSave!: ({done, fail, invalid}: any) => void;

  static initClass(): void {
    this.prototype.debouncedSave = debounce(function (
      this: SequencePrimer,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      {done, fail, invalid}: any
    ): void {
      const xhr = this.save({}, {fileUpdate: true});
      if (xhr) {
        if (done != null) {
          xhr.done(done);
        }
        if (fail != null) {
          xhr.fail(fail);
        }
      } else if (typeof invalid === 'function') {
        invalid(this.validationError);
      }
    }, 500);
  }

  static generateName(
    filename: string,
    start: number,
    end: number,
    direction: Direction,
    seqLength: number,
    startIndex: number
  ): string {
    const pStart = sequtil.modelStartToDisplay(start, startIndex);
    const pEnd = sequtil.modelEndToDisplay(end, seqLength, startIndex);
    const dir = DIRECTION_ABBR[direction];
    const suffix = `${pStart}-${pEnd} ${dir}`;
    // Leave an allowance of 10 characters for additional numbering and primer pair details (from Primer3).
    return `${ellipsize(filename, MAX_PRIMER_NAME_LENGTH - suffix.length - 10)} ${suffix}`;
  }

  override initialize(attributes: SequencePrimerAttributes, options: {}): void {
    super.initialize(attributes, options);
    // convert between U<->T when changing nucleotide type
    this.on('change:nucleotideType', (model: SequencePrimer) => {
      assert(!this.get('primer_id'), 'Toggling nucleotideType only supported for new oligos');
      model.set({bases: util.sanitize(this.bases(), this.nucleotideType())});
    });
  }

  override defaults(): Partial<SequencePrimerAttributes> {
    return {
      name: '',
      bases: '',
      color: colors.randomPastelColor(),
      start: 0,
      end: 1,
      strand: 1,
      overhang_length: 0,
      primer_id: null,
      displayLabel: '',
      nucleotideType: NucleotideType.DNA,
      modifiedNucleotides: [],
    };
  }

  override clone(offset: number = 0): this {
    const newPrimer = super.clone().set({
      start: this.start() + offset,
      end: this.end() + offset,
    });
    if (newPrimer.get('bind_position') != null) {
      newPrimer.set('bind_position', newPrimer.get('bind_position') + offset);
    }
    return newPrimer as this;
  }

  start(): GetAttribute<'start'> {
    return this.get('start');
  }
  end(): GetAttribute<'end'> {
    return this.get('end');
  }
  bases(): GetAttribute<'bases'> {
    return this.get('bases');
  }
  strand(): GetAttribute<'strand'> {
    return this.get('strand');
  }
  name(): GetAttribute<'name'> {
    return this.get('name');
  }
  color(): GetAttribute<'color'> {
    return this.get('color');
  }
  modifiedNucleotides(): GetAttribute<'modifiedNucleotides'> {
    return this.get('modifiedNucleotides');
  }
  nucleotideType(): GetAttribute<'nucleotideType'> {
    return this.get('nucleotideType');
  }
  length(): number {
    return this.bases().length;
  }
  displayLabel(): string {
    // In old versions, displayLabel was not populated, so fall back to name in that case.
    return this.get('displayLabel') || this.name();
  }
  /**
   * Return an identity string for the purpose of determining when an annotation is a duplicate
   * of another.
   */
  identityFingerprint(): string {
    return `${this.start()}:${this.end()}:${this.strand()}:${this.get('primer_api_id')}`;
  }

  // The condition is necessary because a SequencePrimer can come either from a backend sequence_primer
  // or a backend sequence (via the /oligos/search endpoint, in SearchPrimerCollection).
  // TODO(T7585): change sequence_primer's dictionary.
  folder(): MinimalFolderSpecification {
    if (typeof this.get('folder') === 'object') {
      return this.get('folder') as MinimalFolderSpecification;
    } else {
      return {
        name: this.get('folder') as string,
        api_identifier: this.get('folder_api_id')!,
        url: this.get('folder_url')!,
      };
    }
  }

  fromSubPart(): boolean {
    return this.get('fromSubPart') ?? false;
  }
  editable(): boolean {
    return this.id != null && !this.fromSubPart();
  }

  referencesExistingOligo(): boolean {
    return Boolean(this.get('primer_id'));
  }

  bindingBases(): string {
    return this.bases().substring(this.get('overhang_length'));
  }

  hasOverhang(): boolean {
    return this.get('overhang_length') > 0;
  }

  overhangBases(): string {
    return this.bases().substring(0, this.get('overhang_length'));
  }
  overhangLength(): GetAttribute<'overhang_length'> {
    return this.get('overhang_length');
  }
  // Returns the end of the overhang farthest away from bind position, and null if the overhang
  // goes past the sequence.
  // If sequence is not provided or sequence length is 0, will just return possibly out-of-bounds values.
  overhangEdge(sequence: Sequence | null = null): number | null {
    let edge: number;
    if (this.strand() === 1) {
      edge = this.start() - this.overhangLength();
    } else {
      edge = this.end() + this.overhangLength();
    }
    if (!sequence || !sequence.length()) {
      return edge;
    }
    if (!sequence.circular()) {
      // Extends off sequence.
      if (edge < 0 || edge >= sequence.length()) {
        return null;
      }
      // In the case where the primer extends to the end of sequence, we'll get an end of 0
      // and thus an edge within valid range. We need to return null if overhangLength > 0,
      // though, because this primer actually extends off the sequence.
      if (this.strand() === -1 && this.end() === 0 && this.overhangLength() > 0) {
        return null;
      }
    }
    return sequtil.mod(edge, sequence.length());
  }

  bindPosition(sequence: {length(): number}): number {
    return sequtil.primerBindPosition(this.strand(), this.start(), this.end(), sequence.length());
  }

  strandAwareStartPosition(sequence: Sequence): number {
    // The other end of the primer, NOT the bind position.
    if (this.strand() === 1) {
      return this.start();
    }
    return sequtil.mod(this.end() - 1, sequence.length());
  }

  setOverhang(sequence: Sequence, overhang: number): void {
    // eslint-disable-next-line camelcase
    const {start, end, overhang_length} = this._bindPositionInfo(
      sequence,
      this.bindPosition(sequence),
      overhang,
      this.strand(),
      this.bases()
    );
    // eslint-disable-next-line camelcase
    this.set({start, end, overhang_length});
  }

  reverse(sequence: Sequence): void {
    // eslint-disable-next-line camelcase
    const {start, end, overhang_length} = this._bindPositionInfo(
      sequence,
      this.bindPosition(sequence),
      this.get('overhang_length'),
      OPPOSITE_STRAND_OF_DOUBLE_STRANDED[this.strand()],
      this.bases()
    );
    // eslint-disable-next-line camelcase
    this.set({strand: OPPOSITE_STRAND_OF_DOUBLE_STRANDED[this.strand()], start, end, overhang_length});
  }

  changeBases(sequence: Sequence, bases: string): void {
    // eslint-disable-next-line camelcase
    const {start, end, overhang_length} = this._bindPositionInfo(
      sequence,
      this.bindPosition(sequence),
      this.get('overhang_length'),
      this.strand(),
      bases
    );
    // eslint-disable-next-line camelcase
    this.set({start, end, overhang_length, bases, modifiedNucleotides: []});
  }

  setBindPosition(
    sequence: Sequence,
    bindPosition: number,
    overhangLength: number,
    param: {keepOverhang?: boolean} = {}
  ): void {
    const {keepOverhang} = param;
    // eslint-disable-next-line camelcase
    const {start, end, overhang_length} = this._bindPositionInfo(
      sequence,
      bindPosition,
      overhangLength,
      this.strand(),
      this.bases()
    );
    if (keepOverhang) {
      this.set({start, end});
    } else {
      // eslint-disable-next-line camelcase
      this.set({start, end, overhang_length});
    }
  }

  _bindPositionInfo(
    sequence: Sequence,
    bindPosition: number,
    overhangLength: number,
    strand: StrandOfDoubleStranded,
    bases: string
  ): {start: number; end: number; overhang_length: number} {
    let end;
    let start;
    if (bases.length === 0) {
      return {start: bindPosition, end: bindPosition, overhang_length: 0};
    }

    overhangLength = Math.min(overhangLength, bases.length - 1);
    overhangLength = Math.max(overhangLength, bases.length - sequence.length());
    if (!sequence.get('circular')) {
      if (strand === 1) {
        overhangLength = Math.max(bases.length - bindPosition - 1, overhangLength);
      } else {
        overhangLength = Math.max(bases.length - (sequence.length() - bindPosition), overhangLength);
      }
    }

    if (strand === -1) {
      start = bindPosition;
      end = sequtil.mod(bindPosition + (bases.length - overhangLength), sequence.length());
    } else {
      start = sequtil.mod(bindPosition - (bases.length - overhangLength) + 1, sequence.length());
      end = sequtil.mod(bindPosition + 1, sequence.length());
    }

    return {start, end, overhang_length: overhangLength};
  }

  _complementTable(): IupacPartialMapping {
    return NUCLEOTIDE_TYPE_TO_COMPLEMENT_TABLE[this.nucleotideType()];
  }

  basesHtml(sequence: ComponentSequence): string {
    const bases = getUTFlexibleNaturalBasesIfEnabled(
      this.bases(),
      this.modifiedNucleotides(),
      this.nucleotideType(),
      SequenceType.OLIGO
    );
    // Returns HTML string of bases with highlights on matches and labeled ends.
    const primer = this;
    const matchingStatuses = this.baseMatchingStatuses(sequence.get('_sequence'));
    return ReactDOMServer.renderToStaticMarkup(
      <>
        <span className="prime five-prime">5' </span>
        {Array.from(bases).map((base: string, index: number) => (
          <span
            key={index}
            className={classNames({
              'primer-base-overhang': index < primer.overhangLength(),
              'primer-base-match': index >= primer.overhangLength() && matchingStatuses[index],
              'primer-base-no-match': index >= primer.overhangLength() && !matchingStatuses[index],
            })}
          >
            {base}
          </span>
        ))}
        <span className="prime three-prime"> 3'</span>
      </>
    );
  }

  validBases(): boolean {
    return this.length() >= MIN_PRIMER_LENGTH;
  }

  override validate(attrs: SequencePrimerAttributes): string | undefined {
    if (attrs.name.length === 0) {
      return 'Primer name cannot be empty.';
    }
    if (!attrs.bases || attrs.bases.length === 0) {
      return 'Bases cannot be empty.';
    }
    if (!this.validBases()) {
      return `Binding site must be at least ${MIN_PRIMER_LENGTH} bases long.`;
    }
    if (!util.isValidSequence(attrs.bases, this.nucleotideType())) {
      return 'Invalid bases.';
    }
    if (attrs.start < 0) {
      return 'Start must be >= 0.';
    }
    if (attrs.end < 0) {
      return 'End must be >= 0.';
    }
    // eslint-disable-next-line benchlint/no-eqeqeq-null
    if (attrs.color === null) {
      return 'Color must be specified.';
    }
    if (!attrs.folder_api_id) {
      return 'Folder must be specified';
    }
    return undefined;
  }

  // returns base indices of mismatches excluding the overhang
  // indices start from 5' end of primer for fwd strand primers and 3' for rev
  mismatchedIndices(sequence: Sequence): Array<number> {
    const mismatched: Array<number> = [];

    this.baseMatchingStatuses(sequence).forEach((basesMatch: boolean, index: number) => {
      if (!basesMatch) {
        mismatched.push(index);
      }
    });

    if (this.strand() === 1) {
      return mismatched;
    } else {
      // return from 3' perspective for reverse primers - this is useful for rendering because
      // it's the perspective when reading left-to-right on a sequence map
      mismatched.reverse();
      return mismatched.map((index) => this.length() - index - 1);
    }
  }

  meltingTemperature(): number {
    return util.meltingTemperature(this.bindingBases(), this.nucleotideType());
  }

  /*
   * Returns an array of booleans where each value indicates whether the corresponding primer base
   * matches the sequence. The returned array will be the same length as primer.bases().
   * Overhang bases are always considered matching and therefore true.
   */
  baseMatchingStatuses(sequence: Sequence): Array<boolean> {
    if (sequence.length() === 0) {
      // If the sequence has no length, all primer bases are overhang and should be considered matching.
      return Array(this.bases().length).fill(true);
    }

    const masterBases = sequence.bases();
    // sanitize primer bases to sequence nucleotide type in order to match uracil against thymine
    const primerBases = util.sanitize(this.bases(), sequence.nucleotideType());
    const start = this.strandAwareStartPosition(sequence);

    const statuses = Array.from(primerBases).map((primerBase, index) => {
      if (index < this.overhangLength()) {
        return true;
      }
      // multiply by strand so that each loop iteration increments for forward primers (strand is 1)
      // and decrements for reverse primers (strand is -1)
      const masterBaseIndex = sequtil.mod(
        start + (index - this.overhangLength()) * this.strand(),
        sequence.length()
      );

      const complementTable =
        sequence.nucleotideType() === NucleotideType.DNA ? DNA_COMPLEMENT_TABLE : RNA_COMPLEMENT_TABLE;
      const masterBase =
        this.strand() === 1
          ? masterBases[masterBaseIndex]
          : util.complement(masterBases[masterBaseIndex], complementTable);

      // allow for ambiguous bases on the primer, but do not account for degenerate bases in the DNA sequence
      // to avoid matching ranges of N's
      return util.matchWithDegeneracy(masterBase, primerBase.toUpperCase() as IUPAC);
    });

    return statuses;
  }
}
SequencePrimer.initClass();

export class SequencePrimerCollection extends SequenceAttributeCollection<
  SequencePrimer,
  SequencePrimerAttributes
> {
  static override initClass(): void {
    this.prototype.model = SequencePrimer; // should match client/molbio/primers/backbone/SequencePrimers.ts
    this.prototype.attributeName = 'primers';
  }

  batchName(): string {
    return 'sequencePrimers';
  }
}
SequencePrimerCollection.initClass();

// TODO fill in - it's really oligo.to_dict plus the attrs listed below
interface OligoSearchResult {
  id: number;
  api_id: string;
}
interface ParsedOligoSearchResult {
  primer_id: number;
  primer_api_id: string;
}
class SearchPrimerCollection extends Backbone.Collection<SequencePrimer> {
  static initClass(): void {
    this.prototype.url = '/1/api/oligos/search';
    this.prototype.model = SequencePrimer;
  }

  override parse(res: Array<OligoSearchResult>): Array<ParsedOligoSearchResult> {
    return res.map((searchResult: OligoSearchResult) => ({
      // eslint-disable-next-line no-restricted-syntax
      ..._.omit(searchResult, 'id', 'api_id'),
      primer_id: searchResult.id,
      primer_api_id: searchResult.api_id,
    }));
  }
}
SearchPrimerCollection.initClass();

export {
  MIN_PRIMER_LENGTH,
  SequencePrimer as Model,
  SequencePrimerCollection as Collection,
  SearchPrimerCollection,
};
